class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    const dialog = this.querySelector('#CartDrawer');
    const overlay = this.querySelector('#CartDrawer-Overlay');
    
    // Set state to open
    setTimeout(() => {
      this.classList.add('active');
      if (dialog) {
        dialog.setAttribute('data-state', 'open');
        dialog.setAttribute('aria-hidden', 'false');
        dialog.style.pointerEvents = 'auto';
      }
      if (overlay) {
        overlay.setAttribute('data-state', 'open');
        overlay.setAttribute('aria-hidden', 'false');
        overlay.style.pointerEvents = 'auto';
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = dialog?.querySelector('button[onclick*="close"]') || dialog?.querySelector('#CartDrawer-Checkout') || dialog;
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    const dialog = this.querySelector('#CartDrawer');
    const overlay = this.querySelector('#CartDrawer-Overlay');
    
    // Set state to closed
    if (dialog) {
      dialog.setAttribute('data-state', 'closed');
      dialog.setAttribute('aria-hidden', 'true');
      dialog.style.pointerEvents = 'none';
    }
    if (overlay) {
      overlay.setAttribute('data-state', 'closed');
      overlay.setAttribute('aria-hidden', 'true');
      overlay.style.pointerEvents = 'none';
    }
    
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems && cartDrawerItems.classList.contains('is-empty')) {
      cartDrawerItems.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) {
        console.warn(`[CartDrawer] Section element not found: ${section.id} (selector: ${section.selector || 'none'})`);
        return;
      }
      
      const sectionHTML = parsedState.sections?.[section.id];
      if (!sectionHTML) {
        console.warn(`[CartDrawer] Section HTML not found in parsedState for: ${section.id}`);
        return;
      }
      
      const selectorToUse = section.selector || '.shopify-section';
      const innerHTML = this.getSectionInnerHTML(sectionHTML, selectorToUse);
      if (innerHTML) {
        sectionElement.innerHTML = innerHTML;
      } else {
        console.warn(`[CartDrawer] Empty innerHTML for section: ${section.id}`);
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html || typeof html !== 'string') {
      console.warn('[CartDrawer] Invalid HTML provided to getSectionInnerHTML');
      return '';
    }
    
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    let element = parsed.querySelector(selector);
    
    // If selector not found, try to find it in the body
    if (!element && parsed.body) {
      element = parsed.body.querySelector(selector);
    }
    
    // If still not found and selector is an ID, try to find by ID
    if (!element && selector.startsWith('#')) {
      const id = selector.substring(1);
      element = parsed.getElementById(id);
      if (!element && parsed.body) {
        element = parsed.body.querySelector(`#${id}`) || parsed.body.querySelector(`[id="${id}"]`);
      }
    }
    
    // If still not found, check if the root section element matches the selector
    if (!element && parsed.body) {
      const section = parsed.body.querySelector('.shopify-section') || parsed.body.firstElementChild;
      if (section) {
        // Check if the section itself matches the selector
        if (selector.startsWith('#')) {
          const id = selector.substring(1);
          if (section.id === id || section.getAttribute('id') === id) {
            element = section;
          }
        } else if (section.matches && section.matches(selector)) {
          element = section;
        }
      }
    }
    
    if (!element) {
      console.warn(`[CartDrawer] Element not found with selector: ${selector}`);
      // Last resort: if we have a section, return its innerHTML
      if (parsed.body) {
        const section = parsed.body.querySelector('.shopify-section') || parsed.body.firstElementChild;
        if (section) {
          return section.innerHTML;
        }
      }
      return '';
    }
    return element.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
